/* eslint-disable react/no-multi-comp */
/**
 * Created by KOTLIAR on 28.03.17.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Bem from 'bemHelper';
import { I18nHoc } from 'helpers/i18n';
import map from 'lodash/map';
import isArray from 'lodash/isArray';
import isEqual from 'lodash/isEqual';
import isObject from 'lodash/isObject';
import values from 'lodash/values';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import size from 'lodash/size';
import findIndex from 'lodash/findIndex';
import isNil from 'lodash/isNil';
import Icon, { iconTypes } from 'components/icon';
import Button from 'components/button';

import { VALIDATION_VALID } from './form-consts';
import ValidationState from './validationState';
import AbstractInput from './abstractInput';
import InputLabel from './input-label';

import 'styles/base/containers/form/_input-listbox.scss';

import translates from './input-listbox-i18n.json';

/**
 * = InputListBox Component =
 *
 *
 *  How to use:
 *
 *      <Dict
 *        url="Object/Classes"
 *        injectPropName="dictContent"
 *      >
 *        <ComboboxInput
 *          dictKey="id"
 *          dictValue="value_long"
 *         dataBind="field_int_array"
 *         injectPropName="dictContent"
 *       >
 *         <InputListBox
 *           injectPropName="dictContent"
 *           renderer={this.rendererListBoxItem}
 *         />
 *       </ComboboxInput>
 *     </Dict>
 *
 *
 * props description:
 *
 *  dictKey     - prop which transfer the unique identifier
 *                of the fibrant element from InputListBox component
 *                like element[dictKey]. Default value is 'id'.
 *
 *  dictValue   - prop which helps to display the Value
 *                of the fibrant element from InputListBox component
 *                like element[dictValue]. Default value is 'value'.
 *
 *  injectPropName - prop which injects a name of data-props from combobox.
 *                   injectPropName in all wrappers must be the same.
 *
 *  dictSortBy   - prop which helps to sort data by field.
 *
 *  closeFromListBox - if listbox has a combobox wrapper it has a closeFromListBox
 *                     prop. It is a function, it will close combobox.
 *
 *  focusFromListBox - if listbox has a combobox wraper it has a focusFromListBox
 *                     prop. It is a function, it will focus to combobox.
 *
 *
 *  renderer    - function that changes the standard appearance of the button,
 *                the argument to it is passed the whole ListBoxItem.
 *                example:
 *
 *                  rendererListBoxItem(listboxItem, isArrayInput, active) {
 *                     return (
 *                       <div data-block="itemListbox">
 *                         <div data-elem="сontent">
 *                           <span data-elem="text">
 *                              value_long: {listboxItem.value_long},
 *                              isArrayInput: {isArrayInput ? 'YES' : 'NO'},
 *                              active: {active ? 'YES' : 'NO'}
 *                            </span>
 *                          </div>
 *                        </div>
 *                      );
 *                    }
 *
 *
 *
 *  functional:
 *
 *  1) Depending on the type of scheme, the listbox displays buttons
 *     that allow you to make single or multiple selections.
 *
 *  2) Keyboard navigation (with combobox wrapper):
 *             ArrowDown - opens combobox wrapper if it was closed. If combobox wrapper is opened
 *                          ArrowDown will select the next item under the current.
 *
 *             ArrowUp   - opens combobox wrapper if it was closed. If combobox is
 *                          opened ArrowUp will select the next item over the current.
 *
 *             Enter      - multiple selections:
 *                                 opens combobox wrapper if it was closed or closes
 *                                 combobox wrapper if it was opened.
 *                          single selection:
 *                                  opens combobox wrapper if it was closed.
 *                                  If combobox wrapper is opened and item was hovered, Enter
 *                                  will select the current item.
 *
 *             Space      - multiple selections:
 *                                 opens combobox wrapper if it was closed. If combobox wrapper
 *                                 is opened and item was hovered, Space
 *                                  will select the current item.
 *                          single selection:
 *                                 opens combobox wrapper if it was closed or closes
 *                                 combobox wrapper if it was opened.
 *
 *             Escape     - closes combobox wrapper if it was opened.
 *
 */

const bemClassesList = new Bem('formListSelectItems');

@I18nHoc(translates)
class InputListBox extends AbstractInput {
  static defaultProps = {
    dictKey: 'id',
    dictValue: 'value',
    closeFromListBox: null,
    focusFromListBox: null,
    injectPropName: 'listBoxItems',
    hasLabel: false,
    nullValueItem: true,
    schema: {
      isArray: false
    },
    unselectableNonArrayInput: false,
    level: 0,
    maxHeight: true
  };

  static propTypes = {
    ...AbstractInput.propTypes,
    dictKey: PropTypes.string,
    dictValue: PropTypes.string,
    dictSortBy: PropTypes.string,
    injectPropName: PropTypes.string,
    closeFromListBox: PropTypes.func,
    focusFromListBox: PropTypes.func,
    renderer: PropTypes.func,
    inForm: PropTypes.bool,
    hasLabel: PropTypes.bool,
    nullValueItem: PropTypes.bool,
    nullValueLabel: PropTypes.string,
    horizontal: PropTypes.bool,
    radio: PropTypes.bool,
    checkbox: PropTypes.bool,
    multiStep: PropTypes.bool,
    noError: PropTypes.bool,
    resetMultiStepLevels: PropTypes.func,
    unselectableNonArrayInput: PropTypes.bool,
    enableFilter: PropTypes.bool,
    levels: PropTypes.shape({}),
    level: PropTypes.number,
    maxHeight: PropTypes.bool,
    calculatedDirectionCallback: PropTypes.func,
    dictFilter: PropTypes.func,
    singleChecked: PropTypes.bool,
  };

  static contextTypes = {
    desktop: PropTypes.bool
  };

  static modifySelected(initialSel, max, modifier) {
    let newSel = 0;
    if (Math.sign(modifier) < 0) {
      newSel = (initialSel === null || initialSel <= 0) ? max : initialSel - 1;
    } else {
      newSel = initialSel === null ? 0 : initialSel + 1;
    }

    return newSel;
  }

  static getDerivedStateFromProps(props, state) {
    const newState = {
      lastDataSize: size(props[props.injectPropName])
    };

    if (props.enableFilter && size(props[props.injectPropName]) !== state.lastDataSize) {
      newState.selected = 0;
    }

    return newState;
  }

  constructor(props, ctx) {
    super(props, ctx);
    this.handleSelect = this.handleSelect.bind(this);
    this.keyNavigation = this.keyNavigation.bind(this);
    this.scrollWheelHandle = this.scrollWheelHandle.bind(this);
    this.setIgnoreMouseEnter = this.setIgnoreMouseEnter.bind(this);
    this.highlightItemFromMouse = this.highlightItemFromMouse.bind(this);
    this.cancelHighlightItem = this.cancelHighlightItem.bind(this);
    this.resetMultiStepLevels = this.resetMultiStepLevels.bind(this);
    this.deleteChildren = this.deleteChildren.bind(this);

    this.state = {
      selected: null,
      steps: '',
      childIndex: 0,
      hideActive: false
    };
  }

  componentDidMount() {
    if (this.props.calculatedDirectionCallback) {
      // this.inputRef = this.hoveredElement || null;
      if (!this.listboxElement) return;

      let windowWidth = null;
      if (typeof window.innerWidth !== 'undefined') {
        windowWidth = window.innerWidth;
      } else if (typeof document.documentElement !== 'undefined'
        && typeof document.documentElement.clientWidth !==
        'undefined' && document.documentElement.clientWidth !== 0) {
        windowWidth = document.documentElement.clientWidth;
      } else {
        windowWidth = document.getElementsByTagName('body')[0].clientWidth;
      }

      const inputRect = this.listboxElement.getBoundingClientRect();
      const spaceToRight = windowWidth - inputRect.right;
      this.props.calculatedDirectionCallback(spaceToRight);
    }
  }

  componentDidUpdate() {
    if (
      this.ignoreScroll ||
      !this.listboxElement ||
      !this.hoveredElement
    ) return;

    const { enableFilter, injectPropName, dictKey } = this.props;
    if (this.state.selected !== null) {
      const hoveredIndex = enableFilter
        ? findIndex(
          this.props[injectPropName],
          item => item[dictKey] === this.hoveredRef.props.it[dictKey]
        )
        : null;
      const listboxElementRect = this.listboxElement.getBoundingClientRect();
      const hoveredRect = this.hoveredElement.getBoundingClientRect();

      const diffBottom = listboxElementRect.bottom - hoveredRect.bottom;
      if (diffBottom < 0) {
        this.listboxElement.scrollTop = Math.abs(diffBottom) + this.listboxElement.scrollTop;
      }

      const diffTop = listboxElementRect.top - (
        (enableFilter && hoveredIndex === 1)
          ? hoveredRect.top - hoveredRect.height
          : hoveredRect.top
      );
      if (diffTop > 0) {
        this.listboxElement.scrollTop = this.listboxElement.scrollTop - Math.abs(diffTop);
      }

      this.setIgnoreMouseEnter(true);
    }
  }

  modifyInjectedProps() {
    const {
      dictValue, dictKey, dictSortBy, dictFilter,
      injectPropName, nullValueLabel, nullValueItem
    } = this.props;

    let injectedProps = this.props[injectPropName] || [];
    if (dictFilter) injectedProps = dictFilter(injectedProps, dictKey);
    if (isObject(injectedProps)) injectedProps = values(injectedProps);

    let modifiedItems = [...injectedProps];
    if (nullValueItem) {
      modifiedItems.unshift({ [dictKey]: null, [dictValue]: nullValueLabel || this.i18n('notIndicated') });
    }
    if (dictSortBy) modifiedItems = sortBy(modifiedItems, dictSortBy);

    return {
      injectedProps,
      modifiedItems
    };
  }

  handleSelect(item, index) {
    const { schema = {}, dictKey, focusFromListBox, nullValueItem } = this.props;
    const isArrayInput = schema.isArray;
    if (schema && schema.readOnly) return;

    let key = null;
    let { value } = this.props;
    if (typeof item[dictKey] !== 'undefined') { key = item[dictKey]; }
    if (isArrayInput) {
      value = isArray(value) ? value : [];
      const isInSelectedArray = value.indexOf(key) > -1;

      // *** Value, that unset all previous choices
      if (typeof schema.unsetValue !== 'undefined') {
        if (schema.unsetValue === key) {
          value = [];
        } else {
          value = value.filter(some => some !== schema.unsetValue);
        }
      }

      value = isInSelectedArray
        ? value.filter(some => some !== key)
        : [...value, key];
      if (schema.default) {
        this.setState({ selected: index });
      }

      if (nullValueItem) {
        if (index === 0) {
          value = [null];
        } else {
          const nullIndex = value.indexOf(null);
          if (nullIndex > -1) { value.splice(nullIndex, 1); }
        }

        if (value.length === 0) value = [null];
      }

      if (this.props.onChange) {
        if (!item.children || isEmpty(item.children)) this.sendOnChangeValue(value, {});
      }
    } else if (this.props.onChange) {
      if (!this.props.multiStep || !item || !item.children || (this.props.multiStep
          && item.children
          && isEmpty(item.children))) {
        this.sendOnChangeValue(key, item);
      }
    }

    if (item && this.props.multiStep) this.setState({ selected: index, steps: key });
    if (focusFromListBox) focusFromListBox();
  }

  sendOnChangeValue(val, item) {
    const {
      horizontal,
      value,
      unselectableNonArrayInput,
      schema = {}
    } = this.props;
    if (!schema.isArray && unselectableNonArrayInput && val === value) {
      this.props.onChange(null);
    } else {
      this.props.onChange(val, item);
    }
    if (horizontal) this.performValidation(val, item);
  }

  resetMultiStepLevels() {
    if (this.props.resetMultiStepLevels) this.props.resetMultiStepLevels();
    this.setState({ steps: '' });
  }

  deleteChildren(levelToDelete, parentIndex, childIndex) {
    this.setState({
      steps: null,
      selected: parentIndex,
      childIndex,
      childIndex2: { [parentIndex]: childIndex },
      hideActive: false
    });
  }

  passNavigationToChildren(listBoxItems) {
    const listbox = this[`itemListbox-${listBoxItems[this.selected][this.props.dictKey]}`]
      ? this[`itemListbox-${listBoxItems[this.selected][this.props.dictKey]}`].getListBox()
      : null;
    if (listbox && listbox.keyNavigation) return listbox.keyNavigation(event);
  }

  keyNavigation(event, /* needToSetActiveFirst = false */) {
    const { schema = {} } = this.props;
    if (schema && schema.readOnly) return;

    const {
      dictKey, dictValue, injectPropName,
      nullValueItem, nullValueLabel,
      horizontal, enableFilter, multiStep
    } = this.props;

    if (this.ignoreScroll) {
      this.setIgnoreScroll(false);
    }
    if (!this.ignoreMouseEnter) {
      this.setIgnoreMouseEnter(true);
    }
    let sel = this.state.selected;

    let listBoxItems = this.props[injectPropName] || [];
    if (isObject(listBoxItems)) {
      listBoxItems = values(listBoxItems);
    }
    if (nullValueItem && !schema.isArray) {
      listBoxItems.unshift({
        [dictKey]: null, [dictValue]: nullValueLabel || this.i18n('notIndicated')
      });
    }

    const maxIndex = listBoxItems.length - 1;
    this.it = listBoxItems[sel];
    const isArrayInput = schema.isArray;

    switch (event.keyCode) {
      case 38: // top
        this.selected = sel;
        event.preventDefault();
        this.setIgnoreMouseEnter(true);

        if (this.state.steps) {
          event.persist();
          this.passNavigationToChildren(listBoxItems);
        }

        if (!horizontal) {
          sel = InputListBox.modifySelected(sel, maxIndex, -1);
          if (enableFilter && listBoxItems[sel].listboxGroupLabel) {
            if (sel) sel -= 1; else sel = maxIndex;
          }
          this.setState({ selected: sel });
        }
        break;

      case 40: // down
        this.selected = sel;
        event.preventDefault();
        this.setIgnoreMouseEnter(true);

        if (this.state.steps) {
          event.persist();
          this.passNavigationToChildren(listBoxItems);
        }

        if (!horizontal) {
          sel = InputListBox.modifySelected(sel, maxIndex, 1);
          if (sel > maxIndex) sel = 0;
          if (enableFilter && listBoxItems[sel].listboxGroupLabel) sel += 1;
          this.setState({ selected: sel });
        }
        break;

      case 37: // left
        event.preventDefault();
        this.setIgnoreMouseEnter(true);
        this.selected = sel;

        if (this.state.steps) {
          event.persist();
          this.passNavigationToChildren(listBoxItems);
        }

        if (this.props.deleteChildren) {
          this.props.deleteChildren(this.props.level, this.props.index, sel);
        }

        if (horizontal) {
          sel = InputListBox.modifySelected(sel, maxIndex, -1);
          this.setState({ selected: sel });
        }
        break;

      case 39: // right
        if (this.it && !this.it.children && !horizontal) return null;
        event.preventDefault();
        this.setIgnoreMouseEnter(true);

        if (multiStep) {
          event.persist();
          if (/* isArrayInput && */ /* sel !== null && */ !this.state.steps) {
            this.selected = sel;
            this.setState({
              hideActive: false,
              steps: this.it[this.props.dictKey]
            },
            () => this.passNavigationToChildren(listBoxItems));
          } else if (listBoxItems[this.selected]) {
            this.passNavigationToChildren(listBoxItems);
          }
        }

        if (horizontal) {
          sel = InputListBox.modifySelected(sel, maxIndex, 1);
          if (sel > maxIndex) sel = 0;
          this.setState({ selected: sel });
        }
        break;

      case 13: // enter
        event.preventDefault();
        if (multiStep) {
          if (this.state.steps) {
            event.persist();
            this.passNavigationToChildren(listBoxItems);
          }
        }

        if (isArrayInput) {
          if (this.props.closeFromListBox) { this.props.closeFromListBox(); }
        } else if (sel !== null) {
          this.handleSelect(this.it, sel);
        } else if (this.props.closeFromListBox) { this.props.closeFromListBox(); }
        break;

      case 32: // space
        event.preventDefault();
        if (multiStep) {
          if (this.state.steps) {
            event.persist();
            this.passNavigationToChildren(listBoxItems);
          }
        }

        if (isArrayInput && sel !== null) {
          this.handleSelect(this.it, sel);
        } else if (this.props.closeFromListBox) { this.props.closeFromListBox(); }
        break;

      case 27: // escape
        event.preventDefault();
        break;

      default:
      // do nothing
    }
  }

  highlightItemFromMouse(index) {
    if (this.ignoreMouseEnter) return;
    this.setIgnoreScroll(true);
    this.setState({ selected: index });
  }

  cancelHighlightItem() {
    if (this.ignoreMouseEnter) return;
    this.setIgnoreScroll(true);
    this.setState({ selected: null });
  }

  setIgnoreScroll(ignore) {
    this.ignoreScroll = ignore;
  }

  setIgnoreMouseEnter(ignore) {
    this.ignoreMouseEnter = ignore;
  }

  scrollWheelHandle(e) {
    if (!this.listboxElement || !this.listboxElement.contains(e.target)) return;
    const area = this.listboxElement;
    const delta = e.deltaY || e.deltaX || e.deltaMode;

    if (delta < 0 && area.scrollTop === 0) e.preventDefault();

    if (delta > 0 && area.scrollHeight - area.clientHeight - area.scrollTop <= 1) {
      e.preventDefault();
    }
  }

  renderItems(modifiedItems) {
    const { dataBind, dictKey, value, multiStep, level } = this.props;
    const valueIsArray = isArray(value);
    const valIsNull = isNil(value);

    return map(modifiedItems, (it, index) => {
      const groupLabel = it.listboxGroupLabel;
      const hovered = index === this.state.selected;
      let key = null;
      if (typeof it[dictKey] !== 'undefined') key = it[dictKey];
      let active = false;
      const multiWithChildren = multiStep && (it.children && !isEmpty(it.children));

      if (!multiStep || !multiWithChildren) {
        active = valueIsArray
          ? value.indexOf(key) > -1
          : value === key || (valIsNull && isNil(key));
      }
      if (multiWithChildren) {
        active = (this.state.steps || value) === key;
      }

      return (
        <ItemInputListBox
          data-test={`ItemInputListBox${index}`}
          dataBindToItemInputListBox={dataBind || false}
          {...this.props}
          deleteChildren={this.deleteChildren}
          key={index}
          it={it}
          groupLabel={groupLabel}
          index={index}
          hovered={hovered}
          parentLevel={level}
          active={active}
          resetMultiStepLevels={this.resetMultiStepLevels}
          onMouseEnter={this.highlightItemFromMouse}
          onMouseMove={this.setIgnoreMouseEnter}
          onMouseLeave={this.cancelHighlightItem}
          onClick={this.handleSelect}
          onKeyDown={this.keyNavigation}
          ref={(ref) => {
            this.hoveredRef = ref;
            this[`itemListbox-${key}`] = ref;
          }}
          itemRef={hoveredButton => hovered ? this.hoveredElement = hoveredButton : null}
        />
      );
    });
  }

  render() {
    const {
      top, left, white, className,
      schema = {}, value, multiStep,
      inForm, maxHeight, hasLabel, horizontal, radio, checkbox, noError
    } = this.props;
    const validationState =
      this.props.validationState ? this.props.validationState.state : VALIDATION_VALID;
    const mods = {
      top,
      left,
      white,
      inForm,
      noMaxHeight: !maxHeight,
      horizontal,
      required: schema.required,
      array: schema.isArray,
      radio,
      checkbox,
      error: horizontal && validationState !== VALIDATION_VALID,
      disabled: schema.readOnly
    };
    const { injectedProps, modifiedItems } = this.modifyInjectedProps();

    // TODO Do we need validationState ?
    /* const validationState =
      this.props.validationState ? this.props.validationState.state : VALIDATION_VALID;
   let tmpValidationStyle = {};
    if (validationState !== VALIDATION_VALID) {
      tmpValidationStyle = { backgroundColor: '#ffe1e1' };
    } */
    const withoutScroll = horizontal || radio || checkbox;
    const noScrollOutside = (this.context.desktop === false)
      ? !(withoutScroll || !this.context.desktop)
      : !withoutScroll;

    return (
      <div
        {...bemClassesList(null, mods, className)}
        ref={listbox => this.listboxElement = listbox}
        onWheel={noScrollOutside ? this.scrollWheelHandle : null}
      >
        {hasLabel &&
          <InputLabel
            schema={schema}
            value={value}
            validationState={this.props.validationState}
            horizontal={horizontal}
          />
        }
        {this.renderItems(modifiedItems)}
        {multiStep && schema.isArray &&
        !(injectedProps[0] && injectedProps[0].children) &&
          <div>
            <button
              onClick={() => {
                this.props.resetMultiStepLevels();
                this.props.closeFromListBox();
              }}
            >
              Готово
            </button>
          </div>
        }
        {horizontal && !noError &&
          <div {...bemClassesList('errorWrapper')}>
            {!this.props.disableValidationState &&
              <ValidationState
                validationState={this.props.validationState}
              />
            }
          </div>
        }
      </div>
    );
  }
}

const bemClassesItem = new Bem('itemListbox');
const bemClassesItemBtn = new Bem('btnListbox');

class ItemInputListBox extends Component {
  static propTypes = {
    className: PropTypes.string,
    dataBindToItemInputListBox: PropTypes.any, // eslint-disable-line
    it: PropTypes.shape({}),
    index: PropTypes.oneOfType([
      PropTypes.number,
      PropTypes.string
    ]),
    itemRef: PropTypes.func,
    groupLabel: PropTypes.bool,
    hovered: PropTypes.bool,
    active: PropTypes.bool,
    horizontal: PropTypes.bool,
    radio: PropTypes.bool,
    checkbox: PropTypes.bool,
    onMouseEnter: PropTypes.func,
    onMouseMove: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    renderer: PropTypes.func,
    deleteChildren: PropTypes.func,
    inForm: PropTypes.bool,
    dictValue: PropTypes.string,
    injectPropName: PropTypes.string,
    nullValueLabel: PropTypes.string,
    schema: PropTypes.shape({}),
    multiStep: PropTypes.bool,
    parentLevel: PropTypes.number,
    levels: PropTypes.shape({}),
    level: PropTypes.number,
    dictKey: PropTypes.string,
    singleChecked: PropTypes.bool,
  };

  constructor(props, context) {
    super(props, context);
    this.handleMouseEnter = this.handleMouseEnter.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.getListBox = this.getListBox.bind(this);
    this.calculatedDirection = this.calculatedDirection.bind(this);

    this.state = {
      calculatedDirection: false
    };
  }

  shouldComponentUpdate({ levels = {}, active, hovered, schema = {}, it }, nextState) {
    const {
      it: thisIt, levels: thisLevels = {}, level: thisLevel,
      dictKey: thisDictKey, schema: thisSchema = {}
    } = this.props;
    const id = it[thisDictKey];
    const currentLevel = thisLevels[`level${thisLevel}`] || {};
    const nextLevel = levels[`level${thisLevel}`] || {};

    return (
      !isEqual(thisIt, it) ||
      (this.props.active !== active) ||
      (this.props.hovered !== hovered) ||
      (thisLevel >= 0 && currentLevel[id] !== nextLevel[id]) ||
      !isEqual(schema, thisSchema) || !isEqual(this.state, nextState)
    );
  }

  getListBox() {
    if (this[`inputRef${this.props.level + 1}`]) return this[`inputRef${this.props.level + 1}`];
    return null;
  }

  handleMouseEnter() {
    if (this.props.onMouseEnter) this.props.onMouseEnter(this.props.index);
  }

  handleMouseMove() {
    if (this.props.onMouseMove) this.props.onMouseMove(false);
  }

  handleMouseLeave(e) {
    if (/*this.props.horizontal && */this.props.onMouseLeave) this.props.onMouseLeave(e);
  }

  handleKeyDown(event) {
    if (this.props.onKeyDown) this.props.onKeyDown(event);
  }

  calculatedDirection(spaceToRight) {
    if (spaceToRight < 0) {
      this.setState({
        directionHorisontal: 'left',
        listBoxChildIsVisible: true
      });
    } else {
      this.setState({
        directionHorisontal: 'right',
        listBoxChildIsVisible: true
      });
    }
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const { onClick, groupLabel, it, index } = this.props;

    if (it.children && !isEmpty(it.children)) {
      const listBox = this.getListBox();
      if (!listBox || (listBox && !listBox.calculated)) {
        this.setState({
          directionHorisontal: 'right',
          listBoxChildIsVisible: false
        });
      }
      this.forceUpdate();
    }
    if (onClick && !(it.noMatchesFound || groupLabel)) onClick(it, index);
  }

  radioCheckbox() {
    const { radio, checkbox, active } = this.props;

    if (radio || checkbox) {
      let iconType = '';
      if (radio) iconType = !active ? iconTypes.radiobox : iconTypes.radioboxChecked;
      if (checkbox) iconType = !active ? iconTypes.checkbox : iconTypes.checkboxChecked;

      return <Icon {...bemClassesItem('icon')} type={iconType} />;
    }

    return null;
  }

  renderNextStepInfo(isArrayInput) {
    const { it, dictKey, levels = {}, level } = this.props;
    const childrenCount = (levels[`level${level}`] && levels[`level${level}`][it[dictKey]]) || null;

    return (
      <React.Fragment>
        {childrenCount && <span {...bemClassesItem('text', 'second')}>{isArrayInput && childrenCount}</span>}
        <Icon {...bemClassesItem('icon', 'second hasChildren')} type={iconTypes.carretRight} />
      </React.Fragment>
    );
  }

  renderItemContent() {
    const {
      dictKey, hovered, className, // eslint-disable-line
      dictValue, schema = {}, injectPropName, renderer, groupLabel,
      horizontal, inForm, radio, checkbox, multiStep, nullValueLabel,
      it = {}, itemRef, active,
      ...restProps
    } = this.props;
    const isArrayInput = schema.isArray;
    if (renderer) return renderer(it, isArrayInput, active);

    const label = it[dictValue];

    const noInteractive = !(it.noMatchesFound || groupLabel);
    const hasChildren = !!it.children && !isEmpty(it.children);
    const direction = this.state.directionHorisontal || 'left';

    return (
      <span {...bemClassesItem('itemListbox')} ref={itemRef} data-test="itemListbox">
        <span {...bemClassesItem('content')}>
          {(
            (!multiStep && !checkbox) ||
            (multiStep && !hasChildren /* && label !== nullValueLabel */)
          )
          && ((isArrayInput || inForm) || this.props.singleChecked) &&
          !horizontal && noInteractive &&
          (active
            ? <Icon {...bemClassesItem('icon')} type={iconTypes.check} />
            : <span {...bemClassesItem('icon')} />
          )}
          {!multiStep && this.radioCheckbox()}
          <span {...bemClassesItem('text')}>{label}</span>
          {multiStep && hasChildren && !horizontal && !radio &&
            this.renderNextStepInfo(isArrayInput)
          }
        </span>
        {multiStep && active && hasChildren &&
          <span
            {...bemClassesItem(
              'child',
              {
                left: direction === 'left',
                visible: this.state.listBoxChildIsVisible
              }
            )}
          >
            {React.cloneElement(<InputListBox />, {
              ...restProps,
              className: 'itemListbox_internal',
              parentIndex: this.props.index,
              schema,
              nullValueItem: false,
              inForm,
              multiStep,
              injectPropName,
              [injectPropName]: it.children,
              dictValue,
              ref: ref => this[`inputRef${this.props.level + 1}`] = ref,
              level: this.props.level + 1,
              calculatedDirectionCallback: this.calculatedDirection
            })}
          </span>
        }
      </span>
    );
  }

  render() {
    const {
      className, schema = {}, groupLabel,
      inForm, multiStep, index,
      it = {}, active, hovered
    } = this.props;
    const isArrayInput = schema.isArray;
    const noInteractive = !(it.noMatchesFound || groupLabel);

    const itemContent = this.renderItemContent();
    const actionEvents = {
      onMouseEnter: this.handleMouseEnter,
      onMouseMove: this.handleMouseMove,
      onMouseLeave: this.handleMouseLeave,
      onClick: this.handleClick,
      onKeyDown: this.handleKeyDown
    };

    if (multiStep && it.children) {
      return (
        <div //eslint-disable-line
          {...bemClassesItemBtn(
            null,
            {
              active,
              hovered: !(!isArrayInput && !inForm) && hovered,
              hasChildren: it.children,
              disabled: schema.readOnly,
            },
            className
          )}
          {...actionEvents}
          onMouseDown={event => event.preventDefault()}
          data-test={`ItemInputListBox${+index}`}
        >
          {itemContent}
        </div>
      );
    }

    return (
      <Button
        data-test={this.props.dataBindToItemInputListBox
          ? `${this.props.dataBindToItemInputListBox}_btnListbox_${this.props.index}`
          : `btnListbox_${this.props.index}`
        }
        {...bemClassesItemBtn(
          null,
          {
            active: active && noInteractive,
            hovered: !(!isArrayInput && active && !inForm) && hovered && noInteractive,
            disabled: schema.readOnly,
            noClick: !noInteractive
          },
          className
        )}
        {...actionEvents}
        // onMouseDown={event => event.preventDefault()} // TODO FIREFOX
        customRenderer
      >
        {itemContent}
      </Button>
    );
  }
}

class InputListBoxAutoDict extends Component {
  static propTypes = InputListBox.propTypes;
  constructor(props) {
    super(props);

    this.keyNavigation = this.keyNavigation.bind(this);
    this.resetMultiStepLevels = this.resetMultiStepLevels.bind(this);
  }

  keyNavigation(event) {
    this.inputRef.keyNavigation(event);
  }

  resetMultiStepLevels() {
    this.inputRef.resetMultiStepLevels();
  }

  render() {
    const { schema = {}, enableFilter } = this.props;
    if (!enableFilter && schema.dict) {
      return (
        <InputListBox
          {...this.props}
          listBoxItems={schema.dict}
          ref={ref => this.inputRef = ref}
        />
      );
    }
    return (
      <InputListBox
        {...this.props}
        ref={ref => this.inputRef = ref}
      />
    );
  }
}

export default InputListBoxAutoDict;
